// TODO: Fix TypeScript errors for Invoice/Contract type unions
import { toInputDate } from "@/utils/dateUtils";
import { ContractUploadDialog } from "@/components/dialogs/ContractUploadDialog";
import { InvoiceUploadDialog } from "@/components/dialogs/InvoiceUploadDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { usePayment } from "@/hooks/usePayment";
import { useInvoice } from "@/hooks/useInvoice";
import { useContract } from "@/hooks/useContract";
import { useSellerContract } from "@/hooks/useSellerContract";
import { useFile } from "@/hooks/useFile";
import { useMedia } from "@/hooks/useMedia";
import { Contract, Invoice, PaymentWithDetails } from "@/types/payment";
import { UserDetails } from "@/types/userDetails";
import {
  Check,
  CreditCard,
  Download,
  Edit,
  FileText,
  Info,
  Loader2,
  ScanFace,
  Save,
  Send,
  Trash2,
  Upload,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels, commonButtonLabels } from "@/i18n";
import type { ApiError } from "@/types/carAuctionClient";
import { ListingManagementService } from "@/services/listing/listingManagementService";

// Type guard helpers
const isInvoiceObject = (
  invoice: string | Invoice | null | undefined,
): invoice is Invoice => {
  return typeof invoice === "object" && invoice !== null;
};

const isContractObject = (
  contract: string | Contract | null | undefined,
): contract is Contract => {
  return typeof contract === "object" && contract !== null;
};

const STANDARD_DEALER_FEE_PERCENT = 1;
const STANDARD_PRIVATE_FEE_PERCENT = 1.9;
const MIN_DEALER_FEE = 1500;
const MIN_PRIVATE_FEE = 2900;

const isDealerStandardRate = (feePercent: number) =>
  Math.abs(feePercent - STANDARD_DEALER_FEE_PERCENT) < 0.05;

const sellerMinFeeForBasePercent = (baseFeePercent: number) =>
  isDealerStandardRate(baseFeePercent) ? MIN_DEALER_FEE : MIN_PRIVATE_FEE;

const resolveBaseFeePercent = (feePercent: number) =>
  isDealerStandardRate(feePercent)
    ? STANDARD_DEALER_FEE_PERCENT
    : STANDARD_PRIVATE_FEE_PERCENT;

const calculateSellerFeeAmount = (salePrice: number, feePercentage: number) =>
  Math.round(salePrice * feePercentage);

/**
 * Apply standard rate with min floor. Returns effective % when min kicks in.
 * When applyMinimum is false, uses the given % exactly (admin override).
 */
const resolveSellerFee = (
  salePrice: number,
  feePercent: number,
  opts: { applyMinimum: boolean; baseFeePercent?: number },
) => {
  const baseFeePercent =
    opts.baseFeePercent ?? resolveBaseFeePercent(feePercent);
  const minFee = sellerMinFeeForBasePercent(baseFeePercent);
  const rawFee = calculateSellerFeeAmount(salePrice, feePercent / 100);

  if (!opts.applyMinimum || salePrice <= 0) {
    return {
      feeAmount: rawFee,
      feePercent: Number(feePercent.toFixed(2)),
      netAmount: Math.round(salePrice - rawFee),
      minApplied: false,
      minFee,
      baseFeePercent,
    };
  }

  const feeAtBase = calculateSellerFeeAmount(salePrice, baseFeePercent / 100);
  const feeAmount = Math.max(feeAtBase, minFee);
  const minApplied = feeAmount > feeAtBase;
  const effectivePercent =
    salePrice > 0
      ? Number(((feeAmount / salePrice) * 100).toFixed(2))
      : baseFeePercent;

  return {
    feeAmount,
    feePercent: effectivePercent,
    netAmount: Math.round(salePrice - feeAmount),
    minApplied,
    minFee,
    baseFeePercent,
  };
};

interface ListingDetailsPaymentProps {
  payments: PaymentWithDetails[];
  bidFee?: number | null;
  buyer?: UserDetails | null;
  seller?: UserDetails | null;
}

export const ListingDetailsPayment: React.FC<ListingDetailsPaymentProps> = ({
  payments,
  bidFee,
  buyer,
  seller,
}) => {
  const { language } = useLanguage();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};
  const invoiceUploadI18n = (adminLabels(language).economy?.invoiceUpload ??
    {}) as Record<string, unknown>;
  const btn = commonButtonLabels(language);
  const { downloadFile } = useFile();
  const { getDownloadUrlAsync } = useMedia();
  // Payment editing state
  const [isEditingSellerPayment, setIsEditingSellerPayment] = useState(false);
  const [isEditingBuyerPayment, setIsEditingBuyerPayment] = useState(false);
  const [isEditingBankAccount, setIsEditingBankAccount] = useState(false);
  /** Contractual rate (1.0 or 1.9) — kept when min floor bumps the displayed %. */
  const [sellerBaseFeePercent, setSellerBaseFeePercent] = useState(
    STANDARD_PRIVATE_FEE_PERCENT,
  );
  /** True when displayed % was raised because of the min fee floor. */
  const [sellerFeeMinApplied, setSellerFeeMinApplied] = useState(false);
  /** After admin edits %/net manually, stop auto-applying the min floor. */
  const [sellerFeeManualOverride, setSellerFeeManualOverride] = useState(false);

  // Upload dialog state
  const [showInvoiceUploadDialog, setShowInvoiceUploadDialog] = useState(false);
  const [showContractUploadDialog, setShowContractUploadDialog] =
    useState(false);
  const [showSellerContractUploadDialog, setShowSellerContractUploadDialog] =
    useState(false);
  const [sellerManualSignatureMethod, setSellerManualSignatureMethod] =
    useState<
      "physical_id_check" | "signed_externally" | "no_signature_required" | "other"
    >("physical_id_check");
  const [sellerManualSignatureNote, setSellerManualSignatureNote] = useState("");

  // Confirmation dialog state
  const [showDeleteInvoiceConfirm, setShowDeleteInvoiceConfirm] =
    useState(false);
  const [showDeleteContractConfirm, setShowDeleteContractConfirm] =
    useState(false);

  // Popup editing state
  const [isEditingInvoice, setIsEditingInvoice] = useState(false);
  const [isEditingContract, setIsEditingContract] = useState(false);

  const [invoiceEditForm, setInvoiceEditForm] = useState({
    invoice_number: "",
  });
  const [contractEditForm, setContractEditForm] = useState({
    contract_number: "",
  });
  const [manualSignatureMethod, setManualSignatureMethod] = useState<
    "physical_id_check" | "signed_externally" | "no_signature_required" | "other"
  >("physical_id_check");
  const [manualSignatureNote, setManualSignatureNote] = useState("");
  const [hasInvoiceChanges, setHasInvoiceChanges] = useState(false);
  const [hasContractChanges, setHasContractChanges] = useState(false);
  const [isSendingPaymentReminder, setIsSendingPaymentReminder] =
    useState(false);

  // Validation functions
  const isInvoiceFormValid = () => {
    return invoiceEditForm.invoice_number.trim() !== "";
  };

  const isContractFormValid = () => {
    return contractEditForm.contract_number.trim() !== "";
  };

  //Hooks
  const { updatePaymentAsync, createPaymentAsync, isUpdatingPayment } =
    usePayment();
  const {
    createInvoiceAsync,
    updateInvoiceAsync,
    deleteInvoiceAsync,
    deleteInvoiceFileAsync,
    addInvoiceFileAsync,
    isCreatingInvoice,
    isUpdatingInvoice,
    isDeletingInvoice,
    isDeletingInvoiceFile,
    isAddingInvoiceFile,
  } = useInvoice();
  const {
    createContractAsync,
    updateContractAsync,
    deleteContractAsync,
    deleteContractFileAsync,
    addContractFileAsync,
    requestSignatureAsync,
    markSignatureAsync,
    isCreatingContract,
    isUpdatingContract,
    isDeletingContract,
    isDeletingContractFile,
    isAddingContractFile,
    isRequestingSignature,
    isMarkingSignature,
  } = useContract();
  const {
    useGetSellerContractByListing,
    deleteSellerContractAsync,
    sendSellerContractAsync,
    isSendingSellerContract,
    requestSignatureAsync: requestSellerSignatureAsync,
    isRequestingSignature: isRequestingSellerSignature,
    markSignatureAsync: markSellerSignatureAsync,
    isMarkingSignature: isMarkingSellerSignature,
    isDeletingSellerContract,
  } = useSellerContract();

  const sellerPayment =
    payments.find((p) => p.type === "seller_payment") || null;
  const buyerPayment = payments.find((p) => p.type === "buyer_payment") || null;
  const contractListingId =
    buyerPayment?.listingId ?? sellerPayment?.listingId ?? null;
  const { data: sellerContract, refetch: refetchSellerContract } =
    useGetSellerContractByListing(contractListingId);
  const sellerContractStatus = sellerContract?.signatureStatus ?? "not_requested";
  const sellerIsBankIdVerified = seller?.bankIdStatus === "approved";
  const contractSignatureStatus =
    buyerPayment?.contract?.buyer_signature_status ?? "not_requested";
  const contractSignatureMethod =
    buyerPayment?.contract?.buyer_signature_method ?? null;
  const contractSignedByName =
    buyerPayment?.contract?.buyer_signed_by_name ?? null;
  const buyerIdentityVerificationMethod =
    buyer?.bankIdStatus === "approved"
      ? "bankid"
      : buyer?.kycStatus === "approved"
        ? "didit"
        : "none";
  const buyerVerificationHighlightClass =
    buyerIdentityVerificationMethod === "bankid"
      ? "border-green-200 bg-green-50/80 dark:border-green-800 dark:bg-green-950/50"
      : buyerIdentityVerificationMethod === "didit"
        ? "border-blue-200 bg-blue-50/80 dark:border-blue-800 dark:bg-blue-950/50"
        : "border-amber-200 bg-amber-50/80 dark:border-amber-800 dark:bg-amber-950/50";

  const getContractSignatureStatusLabel = () => {
    if (contractSignatureStatus === "signed") {
      return L.contractSignatureSigned ?? "Signerat";
    }
    if (contractSignatureStatus === "requested") {
      return L.contractSignatureRequested ?? "Signering begärd";
    }
    if (contractSignatureStatus === "declined") {
      return L.contractSignatureDeclined ?? "Signering misslyckad";
    }
    return L.contractSignatureNotRequested ?? "Ingen signering begärd";
  };

  const getContractSignatureMethodLabel = (method?: string | null) => {
    if (method === "bankid_idura") {
      return `${L.contractSignatureMethodBankId ?? "BankID"} via Idura`;
    }
    if (method === "physical_id_check") {
      return L.contractSignatureMethodPhysical ?? "Fysiskt signerat med ID-kontroll";
    }
    if (method === "signed_externally") {
      return L.contractSignatureMethodExternal ?? "Signerat utanför systemet";
    }
    if (method === "no_signature_required") {
      return L.contractSignatureMethodNotRequired ?? "Signering behövs ej";
    }
    if (method === "other") return L.contractSignatureMethodOther ?? "Annan bekräftad metod";
    return L.notSpecified ?? "Ej angivet";
  };

  const buyerMinFee = 2900;

  const parseFeePercent = (raw: unknown, fallback = 1.9): number => {
    const asNum = raw != null && raw !== "" ? Number(raw) : NaN;
    if (!Number.isFinite(asNum)) return fallback;
    // Accept either fraction (0.019) or already-percent (1.9)
    const percent = asNum > 0 && asNum <= 1 ? asNum * 100 : asNum;
    return Number(percent.toFixed(2));
  };

  const sellerBankDetails = sellerPayment
    ? sellerPayment.payout_bankDetails
    : null;

  // Payment form state
  const [sellerPaymentForm, setSellerPaymentForm] = useState({
    status: "unpaid" as
      | "unpaid"
      | "pending"
      | "paid"
      | "failed"
      | "overdue"
      | "cancelled",
    amount: 0,
    /** Fee as percent display value, e.g. 1.9 for 1.9% */
    feePercent: 1.9,
    netAmount: 0,
    comment: "",
  });
  const [buyerPaymentForm, setBuyerPaymentForm] = useState({
    status: "unpaid" as
      | "unpaid"
      | "pending"
      | "paid"
      | "failed"
      | "overdue"
      | "cancelled",
    amount: 0,
    /** Buyer fee as percent of sale price, e.g. 1.5 */
    feePercent: 1.5,
    comment: "",
    due_date: "",
    paid_date: "",
  });
  const [originalSellerPaymentForm, setOriginalSellerPaymentForm] = useState({
    status: "unpaid" as
      | "unpaid"
      | "pending"
      | "paid"
      | "failed"
      | "overdue"
      | "cancelled",
    amount: 0,
    feePercent: 1.9,
    netAmount: 0,
    comment: "",
  });
  const [originalBuyerPaymentForm, setOriginalBuyerPaymentForm] = useState({
    status: "unpaid" as
      | "unpaid"
      | "pending"
      | "paid"
      | "failed"
      | "overdue"
      | "cancelled",
    amount: 0,
    feePercent: 1.5,
    comment: "",
    due_date: "",
    paid_date: "",
  });

  const listingBidFee = bidFee ?? 0;
  const buyerSaleBase =
    sellerPayment?.amount && sellerPayment.amount > 0
      ? sellerPayment.amount
      : Math.max(0, (buyerPayment?.amount ?? 0) - listingBidFee);
  const buyerTotalAmount = buyerPaymentForm.amount;
  const buyerFeeAmount = Math.round(buyerTotalAmount - buyerSaleBase);
  const hasBidFee = listingBidFee > 0 || buyerFeeAmount !== 0;
  const buyerBaseAmount = buyerSaleBase;

  const suggestedBuyerAmount = useMemo(() => {
    // "Förslag" should reflect the system-calculated amount (sale price + buyer fee).
    // We use the current buyerPayment amount as the best available source.
    return typeof buyerPayment?.amount === "number" && buyerPayment.amount > 0
      ? buyerPayment.amount
      : typeof buyerPaymentForm.amount === "number" &&
          buyerPaymentForm.amount > 0
        ? buyerPaymentForm.amount
        : 0;
  }, [buyerPayment?.amount, buyerPaymentForm.amount]);

  // Bank account form state
  const [bankAccountForm, setBankAccountForm] = useState({
    bankName: "",
    accountNumber: "",
    clearingNumber: "",
    accountHolder: "",
  });
  const [originalBankAccountForm, setOriginalBankAccountForm] = useState({
    bankName: "",
    accountNumber: "",
    clearingNumber: "",
    accountHolder: "",
  });

  // Update payment forms when payment data changes
  useEffect(() => {
    if (sellerPayment) {
      const amount = sellerPayment.amount || 0;
      const storedPercent = parseFeePercent(
        sellerPayment.sellerFeePercentage,
        seller?.role === "dealer"
          ? STANDARD_DEALER_FEE_PERCENT
          : STANDARD_PRIVATE_FEE_PERCENT,
      );
      // Prefer seller role for contractual rate; fall back to stored % heuristic.
      const baseFeePercent =
        seller?.role === "dealer"
          ? STANDARD_DEALER_FEE_PERCENT
          : seller?.role === "individual" || seller?.role === "business"
            ? STANDARD_PRIVATE_FEE_PERCENT
            : resolveBaseFeePercent(storedPercent);
      const storedDealerFee =
        sellerPayment.dealerFee != null ? Number(sellerPayment.dealerFee) : null;
      const storedNet =
        sellerPayment.netAmount != null ? Number(sellerPayment.netAmount) : null;

      let nextFeePercent: number;
      let nextNet: number;
      let minApplied = false;
      let manualOverride = false;

      const autoResolved = resolveSellerFee(amount, baseFeePercent, {
        applyMinimum: true,
        baseFeePercent,
      });
      const minFee = sellerMinFeeForBasePercent(baseFeePercent);

      if (
        storedDealerFee != null &&
        Number.isFinite(storedDealerFee) &&
        storedNet != null &&
        Number.isFinite(storedNet)
      ) {
        // Prefer persisted amounts; derive display % from them
        nextFeePercent =
          amount > 0
            ? Number(((storedDealerFee / amount) * 100).toFixed(2))
            : baseFeePercent;
        nextNet = Math.round(storedNet);
        minApplied =
          amount > 0 &&
          Math.abs(storedDealerFee - minFee) < 1 &&
          autoResolved.minApplied;
        // Custom fee/net that isn't the auto min-floor result
        manualOverride =
          Math.abs(storedDealerFee - autoResolved.feeAmount) > 1;
      } else {
        // No persisted amounts — use stored listing % if it differs from base
        // (admin override), otherwise apply min floor on the contractual rate.
        const storedIsBase =
          Math.abs(storedPercent - baseFeePercent) < 0.05;
        if (storedIsBase) {
          nextFeePercent = autoResolved.feePercent;
          nextNet = autoResolved.netAmount;
          minApplied = autoResolved.minApplied;
        } else {
          const exact = resolveSellerFee(amount, storedPercent, {
            applyMinimum: false,
            baseFeePercent,
          });
          nextFeePercent = exact.feePercent;
          nextNet = exact.netAmount;
          manualOverride = true;
        }
      }

      const next = {
        status: sellerPayment.status,
        amount,
        feePercent: nextFeePercent,
        netAmount: nextNet,
        comment: sellerPayment.comment || "",
      };
      setSellerPaymentForm(next);
      setOriginalSellerPaymentForm(next);
      setSellerBaseFeePercent(baseFeePercent);
      setSellerFeeMinApplied(minApplied);
      setSellerFeeManualOverride(manualOverride);
    }
  }, [sellerPayment, seller?.role]);

  // Update bank account form when sellerBankDetails changes
  useEffect(() => {
    if (sellerBankDetails) {
      setBankAccountForm({
        bankName: sellerBankDetails.bank || "",
        accountNumber: sellerBankDetails.accountNumber || "",
        clearingNumber: sellerBankDetails.clearingNumber || "",
        accountHolder: sellerBankDetails.accountHolder || "",
      });
      setOriginalBankAccountForm({
        bankName: sellerBankDetails.bank || "",
        accountNumber: sellerBankDetails.accountNumber || "",
        clearingNumber: sellerBankDetails.clearingNumber || "",
        accountHolder: sellerBankDetails.accountHolder || "",
      });
    }
  }, [sellerBankDetails]);

  // Update invoice edit form when buyerPayment changes
  useEffect(() => {
    if (buyerPayment?.invoice) {
      setInvoiceEditForm({
        invoice_number: buyerPayment.invoice.invoice_number,
      });
      setHasInvoiceChanges(false); // Reset changes flag when form is initialized
    }
  }, [buyerPayment?.invoice]);

  // Update contract edit form when buyerPayment changes
  useEffect(() => {
    if (buyerPayment?.contract) {
      setContractEditForm({
        contract_number: buyerPayment.contract.contract_number,
      });
      setHasContractChanges(false); // Reset changes flag when form is initialized
    }
  }, [buyerPayment?.contract]);

  useEffect(() => {
    if (buyerPayment) {
      const formatDateForInput = (dateString: string | null | undefined) => {
        if (!dateString) return "";
        return toInputDate(new Date(dateString));
      };

      const total = buyerPayment.amount || 0;
      const saleBase =
        sellerPayment?.amount && sellerPayment.amount > 0
          ? sellerPayment.amount
          : Math.max(0, total - (bidFee ?? 0));
      const feeAmount = Math.round(total - saleBase);
      const feePercent =
        saleBase > 0
          ? Number(((feeAmount / saleBase) * 100).toFixed(2))
          : 1.5;

      const next = {
        status: buyerPayment.status,
        amount: total,
        feePercent,
        comment: buyerPayment.comment || "",
        due_date: formatDateForInput(buyerPayment.due_date),
        paid_date: formatDateForInput(buyerPayment.paid_date),
      };
      setBuyerPaymentForm(next);
      setOriginalBuyerPaymentForm(next);
    }
  }, [buyerPayment, sellerPayment?.amount, bidFee]);

  // Check if seller payment has changes
  const hasSellerPaymentChanges = () => {
    return (
      sellerPaymentForm.status !== originalSellerPaymentForm.status ||
      sellerPaymentForm.amount !== originalSellerPaymentForm.amount ||
      sellerPaymentForm.feePercent !== originalSellerPaymentForm.feePercent ||
      sellerPaymentForm.netAmount !== originalSellerPaymentForm.netAmount ||
      sellerPaymentForm.comment !== originalSellerPaymentForm.comment
    );
  };

  // Check if bank account has changes
  const hasBankAccountChanges = () => {
    return (
      bankAccountForm.bankName !== originalBankAccountForm.bankName ||
      bankAccountForm.accountNumber !== originalBankAccountForm.accountNumber ||
      bankAccountForm.clearingNumber !==
        originalBankAccountForm.clearingNumber ||
      bankAccountForm.accountHolder !== originalBankAccountForm.accountHolder
    );
  };

  // Check if buyer payment has changes
  const hasBuyerPaymentChanges = () => {
    return (
      buyerPaymentForm.status !== originalBuyerPaymentForm.status ||
      buyerPaymentForm.amount !== originalBuyerPaymentForm.amount ||
      buyerPaymentForm.feePercent !== originalBuyerPaymentForm.feePercent ||
      buyerPaymentForm.comment !== originalBuyerPaymentForm.comment ||
      buyerPaymentForm.due_date !== originalBuyerPaymentForm.due_date ||
      buyerPaymentForm.paid_date !== originalBuyerPaymentForm.paid_date
    );
  };

  const sellerFeeAmountLive = calculateSellerFeeAmount(
    sellerPaymentForm.amount,
    sellerPaymentForm.feePercent / 100,
  );
  const sellerMinFee = sellerMinFeeForBasePercent(sellerBaseFeePercent);
  const sellerFeeBelowMin =
    sellerFeeManualOverride &&
    sellerPaymentForm.amount > 0 &&
    sellerFeeAmountLive >= 0 &&
    sellerFeeAmountLive < sellerMinFee;
  const sellerFeeNegative =
    sellerPaymentForm.feePercent < 0 ||
    sellerPaymentForm.netAmount > sellerPaymentForm.amount;

  const buyerFeeBelowMin =
    buyerSaleBase > 0 &&
    buyerFeeAmount >= 0 &&
    buyerFeeAmount < buyerMinFee;
  const buyerFeeNegative =
    buyerPaymentForm.feePercent < 0 || buyerFeeAmount < 0;

  const updateSellerFromSalePrice = (salePrice: number) => {
    if (sellerFeeManualOverride) {
      const dealerFee = calculateSellerFeeAmount(
        salePrice,
        sellerPaymentForm.feePercent / 100,
      );
      setSellerPaymentForm((prev) => ({
        ...prev,
        amount: salePrice,
        netAmount: Math.round(salePrice - dealerFee),
      }));
      return;
    }

    const resolved = resolveSellerFee(salePrice, sellerBaseFeePercent, {
      applyMinimum: true,
      baseFeePercent: sellerBaseFeePercent,
    });
    setSellerFeeMinApplied(resolved.minApplied);
    setSellerPaymentForm((prev) => ({
      ...prev,
      amount: salePrice,
      feePercent: resolved.feePercent,
      netAmount: resolved.netAmount,
    }));
  };

  const updateSellerFromFeePercent = (feePercent: number) => {
    const salePrice = sellerPaymentForm.amount;
    const dealerFee = calculateSellerFeeAmount(salePrice, feePercent / 100);
    setSellerFeeManualOverride(true);
    setSellerFeeMinApplied(false);
    setSellerPaymentForm((prev) => ({
      ...prev,
      feePercent,
      netAmount: Math.round(salePrice - dealerFee),
    }));
  };

  const updateSellerFromNet = (netAmount: number) => {
    const salePrice = sellerPaymentForm.amount;
    const dealerFee = Math.round(salePrice - netAmount);
    const feePercent =
      salePrice > 0 ? Number(((dealerFee / salePrice) * 100).toFixed(2)) : 0;
    setSellerFeeManualOverride(true);
    setSellerFeeMinApplied(false);
    setSellerPaymentForm((prev) => ({
      ...prev,
      netAmount,
      feePercent,
    }));
  };

  const updateBuyerFromFeePercent = (feePercent: number) => {
    const feeAmount = Math.round(buyerSaleBase * (feePercent / 100));
    setBuyerPaymentForm((prev) => ({
      ...prev,
      feePercent,
      amount: Math.round(buyerSaleBase + feeAmount),
    }));
  };

  const updateBuyerFromTotal = (total: number) => {
    const feeAmount = Math.round(total - buyerSaleBase);
    const feePercent =
      buyerSaleBase > 0
        ? Number(((feeAmount / buyerSaleBase) * 100).toFixed(2))
        : 0;
    setBuyerPaymentForm((prev) => ({
      ...prev,
      amount: total,
      feePercent,
    }));
  };

  // Save seller payment changes
  const handleSaveSellerPayment = async () => {
    if (!sellerPayment) return;

    const dealerFee = calculateSellerFeeAmount(
      sellerPaymentForm.amount,
      sellerPaymentForm.feePercent / 100,
    );
    const netAmount = Math.round(sellerPaymentForm.amount - dealerFee);
    // Keep contractual rate on the listing when min floor only raised the display %
    const sellerFeePercentage = sellerFeeManualOverride
      ? sellerPaymentForm.feePercent / 100
      : sellerBaseFeePercent / 100;

    try {
      await updatePaymentAsync({
        id: sellerPayment.id,
        listingId: sellerPayment.listingId,
        type: sellerPayment.type,
        status: sellerPaymentForm.status,
        amount: sellerPaymentForm.amount,
        sellerFeePercentage,
        dealerFee,
        netAmount,
        comment: sellerPaymentForm.comment,
        due_date: sellerPayment.due_date,
        paid_date: sellerPayment.paid_date,
        payout_bankDetails: sellerPayment.payout_bankDetails,
        created_at: sellerPayment.created_at,
      });

      setOriginalSellerPaymentForm({
        ...sellerPaymentForm,
        netAmount,
        feePercent: sellerPaymentForm.feePercent,
      });
      setSellerPaymentForm((prev) => ({
        ...prev,
        netAmount,
      }));
      setIsEditingSellerPayment(false);

      toast.success(L.sellerPaymentUpdated ?? "Säljarbetalning uppdaterad!");
    } catch (error) {
      toast.error(
        L.sellerPaymentUpdateError ??
          "Fel vid uppdatering av säljarbetalning. Försök igen.",
      );
    }
  };

  // Save bank account changes
  const handleSaveBankAccount = async () => {
    if (!sellerPayment) return;

    try {
      await updatePaymentAsync({
        id: sellerPayment.id,
        listingId: sellerPayment.listingId,
        type: sellerPayment.type,
        status: sellerPayment.status,
        amount: sellerPayment.amount,
        comment: sellerPayment.comment,
        due_date: sellerPayment.due_date,
        paid_date: sellerPayment.paid_date,
        payout_bankDetails: {
          bank: bankAccountForm.bankName,
          accountNumber: bankAccountForm.accountNumber,
          clearingNumber: bankAccountForm.clearingNumber,
          accountHolder: bankAccountForm.accountHolder,
        },
        created_at: sellerPayment.created_at,
      });

      setOriginalBankAccountForm({ ...bankAccountForm });
      setIsEditingBankAccount(false);
      toast.success(L.bankDetailsUpdated ?? "Bankuppgifter uppdaterade!");
    } catch (error) {
      toast.error(
        L.errorUpdatingBankDetails ??
          "Fel vid uppdatering av bankuppgifter. Försök igen.",
      );
    }
  };

  // Save buyer payment changes
  const handleSaveBuyerPayment = async () => {
    if (!buyerPayment) return;

    const nextBidFee = Math.round(buyerPaymentForm.amount - buyerSaleBase);

    try {
      await updatePaymentAsync({
        id: buyerPayment.id,
        listingId: buyerPayment.listingId,
        type: buyerPayment.type,
        status: buyerPaymentForm.status,
        amount: buyerPaymentForm.amount,
        bidFee: nextBidFee,
        comment: buyerPaymentForm.comment,
        due_date: buyerPaymentForm.due_date || null,
        paid_date: buyerPaymentForm.paid_date || null,
        payout_bankDetails: buyerPayment.payout_bankDetails,
        created_at: buyerPayment.created_at,
      });

      // Keep invoice metadata in sync with payment amount/due date (best-effort)
      if (buyerPayment.invoice?.id) {
        try {
          await updateInvoiceAsync({
            id: buyerPayment.invoice.id,
            // backend fields (not yet in frontend Invoice type)
            ...(buyerPaymentForm.amount != null
              ? ({ totalAmount: buyerPaymentForm.amount } as any)
              : {}),
            ...(buyerPaymentForm.due_date
              ? ({ dueAt: buyerPaymentForm.due_date } as any)
              : {}),
          } as any);
        } catch (e) {}
      }

      // Update original form
      setOriginalBuyerPaymentForm({ ...buyerPaymentForm });

      // Close editing mode
      setIsEditingBuyerPayment(false);

      toast.success(L.buyerPaymentUpdated ?? "Köparbetalning uppdaterad!");
    } catch (error) {
      toast.error(
        L.buyerPaymentUpdateError ??
          "Fel vid uppdatering av köparbetalning. Försök igen.",
      );
    }
  };

  const handleSendReminder = async () => {
    const listingId = buyerPayment?.listingId;
    if (!listingId) {
      toast.error(
        L.reminderSendError ?? "Fel vid skickande av påminnelse. Försök igen.",
      );
      return;
    }
    setIsSendingPaymentReminder(true);
    try {
      await ListingManagementService.sendBuyerPaymentReminder(listingId);
      toast.success(L.reminderSent ?? "Påminnelse skickad till kund!");
    } catch (error) {
      const msg =
        (error as ApiError)?.message ||
        (error as Error)?.message ||
        (L.reminderSendError ??
          "Fel vid skickande av påminnelse. Försök igen.");
      toast.error(msg);
    } finally {
      setIsSendingPaymentReminder(false);
    }
  };

  // Handle invoice upload dialog submission
  const handleInvoiceUpload = async (data: {
    file: File | null;
    invoiceNumber: string;
    customer: string;
    vehicle: string;
    amount: string;
    dueDate: string;
  }) => {
    // Validate all required fields
    if (!data.file) {
      toast.error(L.fileRequired ?? "Du måste välja en fil.");
      return;
    }

    if (!data.invoiceNumber.trim()) {
      toast.error(L.invoiceNumberRequired ?? "Fakturanummer måste fyllas i.");
      return;
    }

    try {
      if (buyerPayment) {
        // Create invoice using useInvoice hook
        await createInvoiceAsync({
          request: {
            listingId: buyerPayment.listingId,
            invoiceNumber: data.invoiceNumber,
            invoiceFile: data.file,
          },
        });
      }

      setShowInvoiceUploadDialog(false);
      toast.success(L.invoiceUploaded ?? "Faktura uppladdad!");
    } catch (error) {
      toast.error(
        L.invoiceUploadError ?? "Fel vid uppladdning av faktura. Försök igen.",
      );
    }
  };

  // Handle contract upload dialog submission
  const handleContractUpload = async (data: {
    file: File | null;
    contractNumber: string;
    customer: string;
    vehicle: string;
    amount: string;
  }) => {
    // Validate all required fields
    if (!data.file) {
      toast.error(L.fileRequired ?? "Du måste välja en fil.");
      return;
    }

    if (!data.contractNumber.trim()) {
      toast.error(
        L.contractNumberRequired ?? "Kontraktsnummer måste fyllas i.",
      );
      return;
    }

    try {
      if (buyerPayment) {
        // Create contract using useContract hook
        await createContractAsync({
          request: {
            listingId: buyerPayment.listingId,
            contractNumber: data.contractNumber,
            contractFile: data.file,
          },
        });
      }

      setShowContractUploadDialog(false);
      toast.success(L.contractUploaded ?? "Kontrakt uppladdat!");
    } catch (error) {
      toast.error(
        L.contractUploadError ??
          "Fel vid uppladdning av kontrakt. Försök igen.",
      );
    }
  };

  // Handle invoice edit save
  const handleSaveInvoiceEdit = async () => {
    if (!buyerPayment?.invoice) return;

    // Validate required fields
    if (!invoiceEditForm.invoice_number.trim()) {
      toast.error(L.invoiceNumberRequired ?? "Fakturanummer måste fyllas i.");
      return;
    }

    try {
      // Optimistic update - update UI immediately
      if (buyerPayment?.invoice) {
        buyerPayment.invoice.invoice_number = invoiceEditForm.invoice_number;
      }

      await updateInvoiceAsync({
        id: buyerPayment.invoice.id,
        invoice_number: invoiceEditForm.invoice_number,
        invoice_file: buyerPayment.invoice.invoice_file, // Keep existing file as metadata
      });

      setIsEditingInvoice(false);
      setHasInvoiceChanges(false);

      toast.success(L.invoiceUpdated ?? "Faktura uppdaterad!");
    } catch (error) {
      // Revert optimistic update on error
      if (buyerPayment?.invoice) {
        // Note: In real app, you'd need to restore the original invoice number
        // For now, we'll just show an error
      }
      toast.error(
        L.invoiceUpdateError ?? "Fel vid uppdatering av faktura. Försök igen.",
      );
    }
  };

  // Handle contract edit save
  const handleSaveContractEdit = async () => {
    if (!buyerPayment?.contract) return;

    // Validate required fields
    if (!contractEditForm.contract_number.trim()) {
      toast.error(
        L.contractNumberRequired ?? "Kontraktsnummer måste fyllas i.",
      );
      return;
    }

    try {
      // Optimistic update - update UI immediately
      if (buyerPayment?.contract) {
        buyerPayment.contract.contract_number =
          contractEditForm.contract_number;
      }

      await updateContractAsync({
        id: buyerPayment.contract.id,
        contract_number: contractEditForm.contract_number,
        contract_file: buyerPayment.contract.contract_file, // Keep existing file as metadata
      });

      setIsEditingContract(false);
      setHasContractChanges(false);

      toast.success(L.contractUpdated ?? "Kontrakt uppdaterat!");
    } catch (error) {
      // Revert optimistic update on error
      if (buyerPayment?.contract) {
        // Note: In real app, you'd need to restore the original contract number
        // For now, we'll just show an error
      }
      toast.error(
        L.contractUpdateError ??
          "Fel vid uppdatering av kontrakt. Försök igen.",
      );
    }
  };

  const handleRequestContractSignature = async () => {
    if (!buyerPayment?.contract) return;
    if (!buyerPayment.contract.contract_file) {
      toast.error(L.contractSignatureRequiresFile ?? "Du måste ladda upp en kontraktsfil innan signering kan begäras.");
      return;
    }

    try {
      const updated = await requestSignatureAsync({
        contractId: buyerPayment.contract.id,
      });
      buyerPayment.contract = updated;
      toast.success(L.contractSignatureRequestedSuccess ?? "Signering har begärts och köparen har notifierats.");
    } catch (error) {
      console.error("Error requesting contract signature:", error);
      toast.error(
        L.contractSignatureRequestedError ??
          "Det gick inte att begära signering just nu. Försök igen.",
      );
    }
  };

  const handleMarkManualContractSignature = async () => {
    if (!buyerPayment?.contract) return;

    try {
      const updated = await markSignatureAsync({
        contractId: buyerPayment.contract.id,
        method: manualSignatureMethod,
        note: manualSignatureNote,
      });
      buyerPayment.contract = updated;
      toast.success(L.contractSignatureMarkedSuccess ?? "Kontraktet markerades som signerat.");
    } catch (error) {
      console.error("Error marking contract signature:", error);
      toast.error(
        L.contractSignatureMarkedError ??
          "Det gick inte att uppdatera signeringsstatusen. Försök igen.",
      );
    }
  };

  // ---- Seller contract handlers ----
  const getSellerContractStatusLabel = () => {
    if (sellerContractStatus === "signed") {
      return L.contractSignatureSigned ?? "Signerat";
    }
    if (sellerContractStatus === "requested") {
      return L.contractSignatureRequested ?? "Signering begärd";
    }
    if (sellerContractStatus === "declined") {
      return L.contractSignatureDeclined ?? "Signering misslyckad";
    }
    return L.contractSignatureNotRequested ?? "Ingen signering begärd";
  };

  const handleSendSellerContract = async () => {
    if (!sellerContract) return;
    try {
      await sendSellerContractAsync(sellerContract.id);
      toast.success(
        L.sellerContractSentSuccess ?? "Säljarkontraktet har skickats till säljaren.",
      );
    } catch (error) {
      console.error("Error sending seller contract:", error);
      toast.error(
        L.sellerContractSentError ??
          "Det gick inte att skicka säljarkontraktet. Försök igen.",
      );
    }
  };

  const handleRequestSellerContractSignature = async () => {
    if (!sellerContract) return;
    if (!sellerContract.document) {
      toast.error(
        L.contractSignatureRequiresFile ??
          "Du måste ladda upp en kontraktsfil innan signering kan begäras.",
      );
      return;
    }
    try {
      await requestSellerSignatureAsync(sellerContract.id);
      await refetchSellerContract();
      toast.success(
        L.sellerContractSignatureRequestedSuccess ??
          "Signering har begärts och säljaren har notifierats.",
      );
    } catch (error) {
      console.error("Error requesting seller contract signature:", error);
      toast.error(
        L.contractSignatureRequestedError ??
          "Det gick inte att begära signering just nu. Försök igen.",
      );
    }
  };

  const handleMarkSellerContractSignature = async () => {
    if (!sellerContract) return;
    try {
      await markSellerSignatureAsync({
        id: sellerContract.id,
        method: sellerManualSignatureMethod,
        note: sellerManualSignatureNote,
      });
      await refetchSellerContract();
      toast.success(
        L.sellerContractSignatureMarkedSuccess ??
          "Säljarkontraktet markerades som signerat.",
      );
    } catch (error) {
      console.error("Error marking seller contract signature:", error);
      toast.error(
        L.contractSignatureMarkedError ??
          "Det gick inte att uppdatera signeringsstatusen. Försök igen.",
      );
    }
  };

  const handleDeleteSellerContract = async () => {
    if (!sellerContract) return;
    if (
      !window.confirm(
        L.deleteSellerContractConfirm ??
          "Är du säker på att du vill radera säljarkontraktet?",
      )
    ) {
      return;
    }
    try {
      await deleteSellerContractAsync(sellerContract.id);
      await refetchSellerContract();
      toast.success(L.sellerContractDeleted ?? "Säljarkontraktet raderades.");
    } catch (error) {
      console.error("Error deleting seller contract:", error);
      toast.error(
        L.contractDeleteError ?? "Fel vid radering av kontrakt. Försök igen.",
      );
    }
  };

  const handleDownloadSellerContract = async () => {
    const doc = sellerContract?.document;
    if (!doc) return;
    try {
      const url = await getDownloadUrlAsync({
        mediaId: doc.mediaId,
        expiresIn: 3600,
      });
      const filename = await downloadFile(url, doc.name || "sellerkontrakt.pdf");
      toast.success(`${L.downloadStarted ?? "Nedladdning startad"}: ${filename}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : (L.unknownError ?? "Okänt fel"));
    }
  };

  // Handle invoice download
  const handleDownloadInvoice = async () => {
    const currentFile = buyerPayment?.invoice?.invoice_file;
    if (currentFile) {
      try {
        // If mediaId exists, fetch presigned URL on-demand via hook
        let downloadUrl = currentFile.url;
        if (currentFile.mediaId && !currentFile.url) {
          downloadUrl = await getDownloadUrlAsync({
            mediaId: currentFile.mediaId,
            expiresIn: 3600,
          });
        }
        const filename = await downloadFile(
          downloadUrl,
          currentFile.name || "faktura.pdf",
        );
        toast.success(
          `${L.downloadStarted ?? "Nedladdning startad"}: ${filename}`,
        );
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : (L.unknownError ?? "Okänt fel");
        toast.error(
          (
            L.invoiceDownloadError ?? "Kunde inte ladda ner faktura: {error}"
          ).replace("{error}", errorMessage),
        );
      }
    } else {
      // Open file picker directly
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".pdf,.doc,.docx";
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          handleEditingInvoiceFileUpload(file);
        }
      };
      input.click();
    }
  };

  // Handle contract download
  const handleDownloadContract = async () => {
    const currentFile = buyerPayment?.contract?.contract_file;
    if (currentFile) {
      try {
        // If mediaId exists, fetch presigned URL on-demand via hook
        let downloadUrl = currentFile.url;
        if (currentFile.mediaId && !currentFile.url) {
          downloadUrl = await getDownloadUrlAsync({
            mediaId: currentFile.mediaId,
            expiresIn: 3600,
          });
        }
        const filename = await downloadFile(
          downloadUrl,
          currentFile.name || "kontrakt.pdf",
        );
        toast.success(
          `${L.downloadStarted ?? "Nedladdning startad"}: ${filename}`,
        );
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : (L.unknownError ?? "Okänt fel");
        toast.error(
          (
            L.contractDownloadError ?? "Kunde inte ladda ner kontrakt: {error}"
          ).replace("{error}", errorMessage),
        );
      }
    } else {
      // Open file picker directly
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".pdf,.doc,.docx";
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          handleEditingContractFileUpload(file);
        }
      };
      input.click();
    }
  };

  // Handle delete invoice
  const handleDeleteInvoice = async () => {
    if (!buyerPayment?.invoice) return;

    try {
      // Save the invoice ID before optimistic update
      const invoiceId = buyerPayment.invoice.id;

      // Optimistic update - remove invoice from UI immediately
      if (buyerPayment) {
        buyerPayment.invoice = undefined;
      }

      await deleteInvoiceAsync(invoiceId);

      toast.success(L.invoiceDeleted ?? "Faktura raderad!");
      setShowDeleteInvoiceConfirm(false);
    } catch (error) {
      // Revert optimistic update on error
      if (buyerPayment) {
        // Note: In real app, you'd need to restore the original invoice data
        // For now, we'll just show an error
      }
      toast.error(
        L.invoiceDeleteError ?? "Fel vid radering av faktura. Försök igen.",
      );
    }
  };

  // Handle delete contract
  const handleDeleteContract = async () => {
    if (!buyerPayment?.contract) return;

    try {
      // Save the contract ID before optimistic update
      const contractId = buyerPayment.contract.id;

      // Optimistic update - remove contract from UI immediately
      if (buyerPayment) {
        buyerPayment.contract = undefined;
      }

      await deleteContractAsync(contractId);

      toast.success(L.contractDeleted ?? "Kontrakt raderat!");
      setShowDeleteContractConfirm(false);
    } catch (error) {
      // Revert optimistic update on error
      if (buyerPayment) {
        // Note: In real app, you'd need to restore the original contract data
        // For now, we'll just show an error
      }
      toast.error(
        L.contractDeleteError ?? "Fel vid radering av kontrakt. Försök igen.",
      );
    }
  };

  // Handle delete invoice file only
  const handleDeleteInvoiceFile = async () => {
    if (!buyerPayment?.invoice?.invoice_file) return;

    try {
      // Save the invoice ID before optimistic update
      const invoiceId = buyerPayment.invoice.id;

      // Optimistic update - remove file from UI immediately
      if (buyerPayment?.invoice) {
        buyerPayment.invoice.invoice_file = undefined;
      }

      await deleteInvoiceFileAsync({ invoiceId });

      toast.success(L.invoiceFileDeleted ?? "Fakturafil raderad!");
    } catch (error) {
      // Revert optimistic update on error
      if (buyerPayment?.invoice) {
        // Note: In real app, you'd need to restore the original file data
        // For now, we'll just show an error
      }
      toast.error(
        L.invoiceFileDeleteError ??
          "Fel vid radering av fakturafil. Försök igen.",
      );
    }
  };

  // Handle delete contract file only
  const handleDeleteContractFile = async () => {
    if (!buyerPayment?.contract?.contract_file) return;

    try {
      // Save the contract ID before optimistic update
      const contractId = buyerPayment.contract.id;

      // Optimistic update - remove file from UI immediately
      if (buyerPayment?.contract) {
        buyerPayment.contract.contract_file = undefined;
      }

      await deleteContractFileAsync({ contractId });

      toast.success(L.contractFileDeleted ?? "Kontraktsfil raderad!");
    } catch (error) {
      // Revert optimistic update on error
      if (buyerPayment?.contract) {
        // Note: In real app, you'd need to restore the original file data
        // For now, we'll just show an error
      }
      toast.error(
        L.contractFileDeleteError ??
          "Fel vid radering av kontraktsfil. Försök igen.",
      );
    }
  };

  // Handle invoice file upload
  const handleEditingInvoiceFileUpload = async (file: File) => {
    if (!file || !buyerPayment?.invoice) return;

    try {
      // Save the invoice ID
      const invoiceId = buyerPayment.invoice.id;

      const newFile = await addInvoiceFileAsync({
        invoiceId,
        invoiceFile: file,
      });

      // Update UI with the returned file object
      if (buyerPayment.invoice) {
        buyerPayment.invoice.invoice_file = newFile;
      }

      toast.success(L.invoiceFileUploaded ?? "Fakturafil uppladdad!");
    } catch (error) {
      toast.error(
        L.invoiceFileUploadError ??
          "Fel vid uppladdning av fakturafil. Försök igen.",
      );
    }
  };

  // Handle contract file upload
  const handleEditingContractFileUpload = async (file: File) => {
    if (!file || !buyerPayment?.contract) return;

    try {
      // Save the contract ID
      const contractId = buyerPayment.contract.id;

      const newFile = await addContractFileAsync({
        contractId,
        contractFile: file,
      });

      // Update UI with the returned file object
      if (buyerPayment.contract) {
        buyerPayment.contract.contract_file = newFile;
      }

      toast.success(L.contractFileUploaded ?? "Kontraktsfil uppladdad!");
    } catch (error) {
      toast.error(
        L.contractFileUploadError ??
          "Fel vid uppladdning av kontraktsfil. Försök igen.",
      );
    }
  };

  return (
    <div>
      <Card
        className={sellerPaymentForm.status === "paid" && "bg-green-500/20"}
      >
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {L.payoutToSeller ?? "Utbetalning till säljare"}
            {sellerPaymentForm.status === "paid" && (
              <Check className="h-5 w-5 text-green-500" />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Bankuppgifter */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="font-medium text-blue-500">
                  {L.bankDetails ?? "Bankuppgifter"}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditingBankAccount(!isEditingBankAccount)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {isEditingBankAccount ? (L.cancel ?? btn.cancel) : btn.edit}
                </Button>
              </div>

              {isEditingBankAccount ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="bankName">{L.bank ?? "Bank"}</Label>
                      <Input
                        id="bankName"
                        value={bankAccountForm.bankName}
                        onChange={(e) =>
                          setBankAccountForm((prev) => ({
                            ...prev,
                            bankName: e.target.value,
                          }))
                        }
                        placeholder={L.enterBankName ?? "Ange banknamn"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="accountNumber">
                        {L.accountNumber ?? "Kontonummer"}
                      </Label>
                      <Input
                        id="accountNumber"
                        value={bankAccountForm.accountNumber}
                        onChange={(e) =>
                          setBankAccountForm((prev) => ({
                            ...prev,
                            accountNumber: e.target.value,
                          }))
                        }
                        placeholder={L.enterAccountNumber ?? "Ange kontonummer"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clearingNumber">
                        {L.clearingNumber ?? "Clearingnummer"}
                      </Label>
                      <Input
                        id="clearingNumber"
                        value={bankAccountForm.clearingNumber}
                        onChange={(e) =>
                          setBankAccountForm((prev) => ({
                            ...prev,
                            clearingNumber: e.target.value,
                          }))
                        }
                        placeholder={
                          L.enterClearingNumber ?? "Ange clearingnummer"
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="accountHolder">
                        {L.accountHolder ?? "Kontoinnehavare"}
                      </Label>
                      <Input
                        id="accountHolder"
                        value={bankAccountForm.accountHolder}
                        onChange={(e) =>
                          setBankAccountForm((prev) => ({
                            ...prev,
                            accountHolder: e.target.value,
                          }))
                        }
                        placeholder={
                          L.enterAccountHolder ?? "Ange kontoinnehavare"
                        }
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      {L.bank ?? "Bank"}
                    </p>
                    <p className="text-sm">{bankAccountForm.bankName}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      {L.accountNumber ?? "Kontonummer"}
                    </p>
                    <p className="text-sm">{bankAccountForm.accountNumber}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      {L.accountHolder ?? "Kontoinnehavare"}
                    </p>
                    <p className="text-sm">{bankAccountForm.accountHolder}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      {L.clearingNumber ?? "Clearingnummer"}
                    </p>
                    <p className="text-sm">{bankAccountForm.clearingNumber}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Belopp och avgift */}
            <div className="border-t pt-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-blue-500">
                    {L.amount ?? "Belopp"}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (isEditingSellerPayment) {
                        setSellerPaymentForm(originalSellerPaymentForm);
                        const autoResolved = resolveSellerFee(
                          originalSellerPaymentForm.amount,
                          sellerBaseFeePercent,
                          {
                            applyMinimum: true,
                            baseFeePercent: sellerBaseFeePercent,
                          },
                        );
                        const liveFee = calculateSellerFeeAmount(
                          originalSellerPaymentForm.amount,
                          originalSellerPaymentForm.feePercent / 100,
                        );
                        const minFee =
                          sellerMinFeeForBasePercent(sellerBaseFeePercent);
                        setSellerFeeMinApplied(
                          originalSellerPaymentForm.amount > 0 &&
                            Math.abs(liveFee - minFee) < 1 &&
                            autoResolved.minApplied,
                        );
                        setSellerFeeManualOverride(
                          Math.abs(liveFee - autoResolved.feeAmount) > 1,
                        );
                      }
                      setIsEditingSellerPayment(!isEditingSellerPayment);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    {isEditingSellerPayment
                      ? (L.cancel ?? btn.cancel)
                      : btn.edit}
                  </Button>
                </div>

                {isEditingSellerPayment ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="salePrice">
                          {L.salePriceSEK ?? "Försäljningspris (SEK)"}
                        </Label>
                        <Input
                          id="salePrice"
                          type="number"
                          value={sellerPaymentForm.amount}
                          onChange={(e) =>
                            updateSellerFromSalePrice(
                              parseInt(e.target.value) || 0,
                            )
                          }
                          placeholder={
                            L.enterSalePrice ?? "Ange försäljningspris"
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="feePercentage">
                            {L.bileniaFeePercent ?? "Bilenia avgift (%)"}
                          </Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="focus:outline-none"
                              >
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="max-w-xs p-3">
                              <div className="space-y-2.5">
                                <div className="space-y-1">
                                  <p className="font-semibold text-sm">
                                    {L.privateAndBusiness ??
                                      "Privat & Företag"}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    1.9%{" "}
                                    {L.percentOfFinalPrice ?? "av slutpriset"}{" "}
                                    • {L.minimum ?? "Minst"} 2900 kr
                                  </p>
                                </div>
                                <div className="h-px bg-border" />
                                <div className="space-y-1">
                                  <p className="font-semibold text-sm">
                                    {L.dealer ?? "Handlare"}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    1.0%{" "}
                                    {L.percentOfFinalPrice ?? "av slutpriset"}{" "}
                                    • {L.minimum ?? "Minst"} 1500 kr
                                  </p>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <Input
                          id="feePercentage"
                          type="number"
                          step="0.1"
                          value={sellerPaymentForm.feePercent}
                          onChange={(e) =>
                            updateSellerFromFeePercent(
                              parseFloat(e.target.value) || 0,
                            )
                          }
                        />
                        <p className="text-xs text-muted-foreground">
                          {L.bileniaFee ?? "Bilenia avgift"}:{" "}
                          {sellerFeeAmountLive.toLocaleString("sv-SE")} SEK
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="netToSeller">
                        {L.netToSellerSEK ?? "Netto till säljare (SEK)"}
                      </Label>
                      <Input
                        id="netToSeller"
                        type="number"
                        value={sellerPaymentForm.netAmount}
                        onChange={(e) =>
                          updateSellerFromNet(parseInt(e.target.value) || 0)
                        }
                        className="text-green-700 font-medium"
                      />
                    </div>
                    {sellerFeeMinApplied && (
                      <div className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-950 dark:border-sky-500/40 dark:bg-sky-950/40 dark:text-sky-100">
                        <p>
                          {(
                            L.sellerFeeMinimumAppliedInfo ??
                            "Avgiften höjdes till miniminivån ({min} kr), därför visas {effective}% istället för {base}%."
                          )
                            .replace(
                              "{min}",
                              sellerMinFee.toLocaleString("sv-SE"),
                            )
                            .replace(
                              "{effective}",
                              String(sellerPaymentForm.feePercent),
                            )
                            .replace(
                              "{base}",
                              String(sellerBaseFeePercent),
                            )}
                        </p>
                      </div>
                    )}
                    {(sellerFeeBelowMin || sellerFeeNegative) && (
                      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100">
                        {sellerFeeNegative && (
                          <p>
                            {L.sellerFeeNegativeWarning ??
                              "Negativ avgift/procent — netto är högre än försäljningspriset."}
                          </p>
                        )}
                        {sellerFeeBelowMin && (
                          <p>
                            {(
                              L.sellerFeeBelowMinimumWarning ??
                              "Säljaravgift under miniminivå ({min} kr). Fortsätt bara om det är avsiktligt."
                            ).replace(
                              "{min}",
                              sellerMinFee.toLocaleString("sv-SE"),
                            )}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.salePrice ?? "Försäljningspris"}
                      </p>
                      <p className="text-lg font-semibold">
                        {sellerPaymentForm.amount.toLocaleString()} SEK
                      </p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-muted-foreground">
                          {L.bileniaFee ?? "Bilenia avgift"} (
                          {sellerPaymentForm.feePercent.toFixed(1)}%)
                        </p>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="focus:outline-none"
                            >
                              <Info className="h-4 w-4 text-muted-foreground" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="max-w-xs p-3">
                            <div className="space-y-2.5">
                              <div className="space-y-1">
                                <p className="font-semibold text-sm">
                                  {L.privateAndBusiness ?? "Privat & Företag"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  1.9%{" "}
                                  {L.percentOfFinalPrice ?? "av slutpriset"} •{" "}
                                  {L.minimum ?? "Minst"} 2900 kr
                                </p>
                              </div>
                              <div className="h-px bg-border" />
                              <div className="space-y-1">
                                <p className="font-semibold text-sm">
                                  {L.dealer ?? "Handlare"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  1.0%{" "}
                                  {L.percentOfFinalPrice ?? "av slutpriset"} •{" "}
                                  {L.minimum ?? "Minst"} 1500 kr
                                </p>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <p className="text-sm text-red-600">
                        -
                        {sellerFeeAmountLive.toLocaleString()} SEK
                      </p>
                    </div>
                  </div>
                  {sellerFeeMinApplied && (
                    <div className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-950 dark:border-sky-500/40 dark:bg-sky-950/40 dark:text-sky-100">
                      <p>
                        {(
                          L.sellerFeeMinimumAppliedInfo ??
                          "Avgiften höjdes till miniminivån ({min} kr), därför visas {effective}% istället för {base}%."
                        )
                          .replace(
                            "{min}",
                            sellerMinFee.toLocaleString("sv-SE"),
                          )
                          .replace(
                            "{effective}",
                            String(sellerPaymentForm.feePercent),
                          )
                          .replace("{base}", String(sellerBaseFeePercent))}
                      </p>
                    </div>
                  )}
                  </div>
                )}

                {!isEditingSellerPayment && (
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center">
                      <p className="font-medium text-green-600">
                        {L.netToSeller ?? "Netto till säljare"}
                      </p>
                      <p className="text-lg font-semibold text-green-600">
                        {sellerPaymentForm.netAmount.toLocaleString()} SEK
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="border-t pt-4">
              <div className="space-y-2">
                <p className="font-medium text-blue-500">
                  {L.status ?? "Status"}
                </p>
                <div className="flex items-center gap-2">
                  <Select
                    value={sellerPaymentForm.status}
                    onValueChange={(
                      value:
                        | "unpaid"
                        | "pending"
                        | "paid"
                        | "failed"
                        | "cancelled",
                    ) =>
                      setSellerPaymentForm((prev) => ({
                        ...prev,
                        status: value,
                      }))
                    }
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unpaid">
                        {L.unpaid ?? "Obetald"}
                      </SelectItem>
                      <SelectItem value="pending">
                        {L.pendingPayout ?? "Väntar på utbetalning"}
                      </SelectItem>
                      <SelectItem value="paid">
                        {L.paidOut ?? "Utbetald"}
                      </SelectItem>
                      <SelectItem value="failed">
                        {L.failed ?? "Misslyckades"}
                      </SelectItem>
                      <SelectItem value="cancelled">
                        {L.cancelled ?? "Avbruten"}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveSellerPayment}
                    disabled={!hasSellerPaymentChanges() || isUpdatingPayment}
                  >
                    {isUpdatingPayment ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {L.saving ?? "Sparar..."}
                      </>
                    ) : (
                      <>
                        <Edit className="h-4 w-4 mr-2" />
                        {L.saveChanges ?? "Spara ändringar"}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Kommentar */}
            <div className="border-t pt-4">
              <div className="space-y-2">
                <p className="font-medium text-blue-500">
                  {L.comment ?? "Kommentar"}
                </p>
                <Textarea
                  value={sellerPaymentForm.comment}
                  onChange={(e) =>
                    setSellerPaymentForm((prev) => ({
                      ...prev,
                      comment: e.target.value,
                    }))
                  }
                  placeholder={
                    L.addCommentPayout ??
                    "Lägg till kommentar om utbetalningen..."
                  }
                  className="min-h-[80px]"
                />
              </div>
            </div>

            {/* Save buttons for seller payment and bank account */}
            <div className="border-t pt-4">
              <div className="flex justify-end gap-2">
                {isEditingBankAccount && (
                  <Button
                    onClick={handleSaveBankAccount}
                    disabled={!hasBankAccountChanges() || isUpdatingPayment}
                    size="sm"
                  >
                    {isUpdatingPayment ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {L.saving ?? "Sparar..."}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {L.saveBankDetails ?? "Spara bankuppgifter"}
                      </>
                    )}
                  </Button>
                )}
                {isEditingSellerPayment && (
                  <Button
                    onClick={handleSaveSellerPayment}
                    disabled={!hasSellerPaymentChanges()}
                    size="sm"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {L.savePayment ?? "Spara betalning"}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Säljarkontrakt */}
          <Card className="space-y-2 p-4 bg-primary/5">
            <div className="flex items-center mb-6 gap-2">
              <p className="font-medium">
                {L.sellerContract ?? "Säljarkontrakt"}
              </p>
              {sellerContract && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteSellerContract}
                    className="cursor-pointer text-red-600 hover:text-red-700"
                    disabled={isDeletingSellerContract}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {isDeletingSellerContract
                      ? (L.deletingContract ?? "Raderar...")
                      : (L.deleteContract ?? "Radera kontrakt")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSellerContractUploadDialog(true)}
                    className="cursor-pointer"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {L.uploadNewContract ?? "Ladda upp ny kontrakt"}
                  </Button>
                </div>
              )}
            </div>

            {!sellerContract ? (
              <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                <FileText className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-3">
                  {L.noSellerContractFound ?? "Inget säljarkontrakt hittat"}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSellerContractUploadDialog(true)}
                  className="cursor-pointer"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {L.uploadSellerContract ?? "Ladda upp säljarkontrakt"}
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      {L.contractNumber ?? "Kontraktsnummer"}
                    </p>
                    <p className="text-sm">{sellerContract.contractNumber}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      {L.file ?? "Fil"}
                    </p>
                    {sellerContract.document ? (
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-600">
                          {sellerContract.document.name || "Sellerkontrakt.pdf"}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleDownloadSellerContract}
                          className="cursor-pointer p-1 h-auto"
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500">
                          {L.noFile ?? "Ingen fil"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 rounded-lg border bg-background p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.contractSignatureStatus ?? "Signeringsstatus"}
                      </p>
                      <Badge
                        className={
                          sellerContractStatus === "signed"
                            ? "bg-green-100 text-green-800"
                            : sellerContractStatus === "requested"
                              ? "bg-amber-100 text-amber-800"
                              : sellerContractStatus === "declined"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                        }
                      >
                        {getSellerContractStatusLabel()}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.contractSignatureMethod ?? "Metod"}
                      </p>
                      <p className="text-sm">
                        {getContractSignatureMethodLabel(sellerContract.signatureMethod)}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {sellerContractStatus === "signed"
                          ? (L.contractSignatureDate ?? "Signerad")
                          : (L.contractSignatureRequestedAt ?? "Begärd")}
                      </p>
                      <p className="text-sm">
                        {(sellerContractStatus === "signed"
                          ? sellerContract.signedAt
                          : sellerContract.signatureRequestedAt)
                          ? new Date(
                              (sellerContractStatus === "signed"
                                ? sellerContract.signedAt
                                : sellerContract.signatureRequestedAt) as string,
                            ).toLocaleString("sv-SE")
                          : (L.notSpecified ?? "Ej angivet")}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.contractSignatureSignedBy ?? "Signerad av"}
                      </p>
                      <p className="text-sm">
                        {sellerContract.signedByName || (L.notSpecified ?? "Ej angivet")}
                      </p>
                    </div>
                  </div>

                  {sellerContract.signatureNote && (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.contractSignatureNote ?? "Notering"}
                      </p>
                      <p className="text-sm">{sellerContract.signatureNote}</p>
                    </div>
                  )}

                  {sellerContractStatus === "signed" ? (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/50">
                      <p className="font-medium text-green-900 dark:text-green-100">
                        {L.contractSignatureSigned ?? "Signerat"}
                      </p>
                      <p className="mt-1 text-sm text-green-800 dark:text-green-200">
                        {L.sellerSignatureCompletedInfo ??
                          "Säljarkontraktet är färdigt signerat. Inga ytterligare åtgärder krävs från admin."}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-lg border p-3 space-y-3">
                        <div>
                          <p className="font-medium">
                            {L.requestContractSignature ?? "Begär BankID-signering"}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {L.requestSellerContractSignatureDesc ??
                              "Skickar mejl, notis och push till säljaren. Säljaren signerar med BankID under Mina annonser."}
                          </p>
                        </div>
                        <div
                          className={`rounded-md border p-3 ${
                            sellerIsBankIdVerified
                              ? "border-green-200 bg-green-50/80 dark:border-green-800 dark:bg-green-950/50"
                              : "border-amber-200 bg-amber-50/80 dark:border-amber-800 dark:bg-amber-950/50"
                          }`}
                        >
                          <p className="text-sm font-medium">
                            {L.sellerIdentityVerificationLabel ?? "Säljarens identitetsverifiering"}
                          </p>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {sellerIsBankIdVerified
                              ? (L.sellerIdentityVerifiedWithBankId ??
                                  "Säljaren är ID-verifierad med BankID och kan signera kontraktet.")
                              : (L.sellerIdentityNotVerifiedBankId ??
                                  "Säljaren är inte verifierad med BankID ännu och behöver verifiera BankID innan signering.")}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            onClick={handleSendSellerContract}
                            disabled={
                              !sellerContract.document || isSendingSellerContract
                            }
                            className="gap-2"
                          >
                            <Send className="h-4 w-4" />
                            {isSendingSellerContract
                              ? (L.sendingSellerContract ?? "Skickar...")
                              : (L.sendSellerContract ?? "Skicka till säljaren")}
                          </Button>
                          <Button
                            onClick={handleRequestSellerContractSignature}
                            disabled={
                              !sellerContract.document ||
                              isRequestingSellerSignature
                            }
                            className="gap-2"
                          >
                            <img
                              src="/assets/BankID_logo.png"
                              alt="BankID"
                              className="h-4 w-auto object-contain"
                            />
                            {isRequestingSellerSignature
                              ? (L.requestingContractSignature ?? "Begär...")
                              : (L.requestContractSignature ?? "Begär BankID-signering")}
                          </Button>
                        </div>
                      </div>

                      <div className="rounded-lg border p-3 space-y-3">
                        <div>
                          <p className="font-medium">
                            {L.markContractSignature ?? "Markera signerat manuellt"}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {L.markContractSignatureDesc ??
                              "Använd detta om kontraktet signerats eller hanterats utanför BankID-flödet."}
                          </p>
                        </div>
                        <Select
                          value={sellerManualSignatureMethod}
                          onValueChange={(value) =>
                            setSellerManualSignatureMethod(
                              value as
                                | "physical_id_check"
                                | "signed_externally"
                                | "no_signature_required"
                                | "other",
                            )
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="physical_id_check">
                              {L.contractSignatureMethodPhysical ??
                                "Fysiskt signerat med ID-kontroll"}
                            </SelectItem>
                            <SelectItem value="signed_externally">
                              {L.contractSignatureMethodExternal ??
                                "Signerat utanför systemet"}
                            </SelectItem>
                            <SelectItem value="no_signature_required">
                              {L.contractSignatureMethodNotRequired ??
                                "Signering behövs ej"}
                            </SelectItem>
                            <SelectItem value="other">
                              {L.contractSignatureMethodOther ??
                                "Annan bekräftad metod"}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <Textarea
                          value={sellerManualSignatureNote}
                          onChange={(e) =>
                            setSellerManualSignatureNote(e.target.value)
                          }
                          placeholder={
                            L.contractSignatureNotePlaceholder ??
                            "Valfri intern notering om hur kontraktet signerades."
                          }
                        />
                        <Button
                          variant="outline"
                          onClick={handleMarkSellerContractSignature}
                          disabled={isMarkingSellerSignature}
                        >
                          {isMarkingSellerSignature
                            ? (L.markingContractSignature ?? "Uppdaterar...")
                            : (L.markContractSignature ?? "Markera signerat manuellt")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </Card>

        </CardContent>
      </Card>

      <div className="my-4" />

      {/* 2. Inbetalning från köpare */}
      <Card className={buyerPaymentForm.status === "paid" && "bg-green-500/20"}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {L.paymentFromBuyer ?? "Inbetalning från köpare"}
              {buyerPaymentForm.status === "paid" && (
                <Check className="h-5 w-5 text-green-500" />
              )}
            </CardTitle>
            <div className="flex flex-wrap gap-2 max-w-full">
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 min-w-0 max-w-full whitespace-normal h-auto min-h-9"
                onClick={() => setIsEditingBuyerPayment(!isEditingBuyerPayment)}
              >
                <Edit className="h-4 w-4 mr-2" />
                {isEditingBuyerPayment
                  ? (L.cancel ?? btn.cancel)
                  : (L.editBuyerPayment ?? "Redigera inbetalning")}
              </Button>
              {buyerPaymentForm.status !== "paid" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendReminder}
                  disabled={
                    isSendingPaymentReminder || !buyerPayment?.listingId
                  }
                  className="text-orange-600 border-orange-200 shrink-0 min-w-0 max-w-full whitespace-normal h-auto min-h-9"
                >
                  {isSendingPaymentReminder ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  {L.sendReminderToCustomer ?? "Skicka påminnelse till kund"}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Betalningsspecifika fält */}
            <div className="space-y-2">
              <p className="font-medium text-blue-500">
                {L.paymentInformation ?? "Betalningsinformation"}
              </p>
              {isEditingBuyerPayment ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.paymentStatus ?? "Betalningsstatus"}
                      </p>
                      <Select
                        value={buyerPaymentForm.status}
                        onValueChange={(
                          value:
                            | "unpaid"
                            | "pending"
                            | "paid"
                            | "failed"
                            | "cancelled",
                        ) =>
                          setBuyerPaymentForm((prev) => ({
                            ...prev,
                            status: value,
                          }))
                        }
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unpaid">
                            {L.unpaid ?? "Obetald"}
                          </SelectItem>
                          <SelectItem value="pending">
                            {L.pendingPayment ?? "Väntar på betalning"}
                          </SelectItem>
                          <SelectItem value="paid">
                            {L.paid ?? "Betald"}
                          </SelectItem>
                          <SelectItem value="failed">
                            {L.failed ?? "Misslyckades"}
                          </SelectItem>
                          <SelectItem value="cancelled">
                            {L.cancelled ?? "Avbruten"}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.salePrice ?? "Försäljningspris"}
                      </p>
                      <p className="text-sm font-medium">
                        {buyerSaleBase.toLocaleString("sv-SE")} SEK
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.buyerFeePercent ?? "Köparavgift (%)"}
                      </p>
                      <Input
                        type="number"
                        step="0.1"
                        value={buyerPaymentForm.feePercent}
                        onChange={(e) =>
                          updateBuyerFromFeePercent(
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        className="w-48"
                      />
                      <p className="text-xs text-muted-foreground">
                        {L.buyerFeeAmount ?? "Köparavgift (SEK)"}:{" "}
                        {buyerFeeAmount.toLocaleString("sv-SE")} SEK
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.buyerTotalAmount ?? "Totalt belopp (SEK)"}
                      </p>
                      <Input
                        type="number"
                        value={buyerPaymentForm.amount}
                        onChange={(e) =>
                          updateBuyerFromTotal(parseInt(e.target.value) || 0)
                        }
                        className="w-48"
                      />
                    </div>
                  </div>
                  {(buyerFeeBelowMin || buyerFeeNegative) && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1">
                      {buyerFeeNegative && (
                        <p>
                          {L.feeNegativeWarning ??
                            "Negativ avgift/procent — köpare betalar mindre än försäljningspriset."}
                        </p>
                      )}
                      {buyerFeeBelowMin && (
                        <p>
                          {(
                            L.feeBelowMinimumWarning ??
                            "Avgift under miniminivå ({min} kr). Fortsätt bara om det är avsiktligt."
                          ).replace(
                            "{min}",
                            buyerMinFee.toLocaleString("sv-SE"),
                          )}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.dueDate ?? "Förfallodatum"}
                      </p>
                      <Input
                        type="date"
                        value={buyerPaymentForm.due_date}
                        onChange={(e) =>
                          setBuyerPaymentForm((prev) => ({
                            ...prev,
                            due_date: e.target.value,
                          }))
                        }
                        className="w-48"
                      />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.paymentDate ?? "Betalningsdatum"}
                      </p>
                      <Input
                        type="date"
                        value={buyerPaymentForm.paid_date}
                        onChange={(e) =>
                          setBuyerPaymentForm((prev) => ({
                            ...prev,
                            paid_date: e.target.value,
                          }))
                        }
                        className="w-48"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      {L.comment ?? "Kommentar"}
                    </p>
                    <Textarea
                      value={buyerPaymentForm.comment}
                      onChange={(e) =>
                        setBuyerPaymentForm((prev) => ({
                          ...prev,
                          comment: e.target.value,
                        }))
                      }
                      placeholder={L.addComment ?? "Lägg till kommentar..."}
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveBuyerPayment}
                      disabled={!hasBuyerPaymentChanges() || isUpdatingPayment}
                      className="bg-blue-500 hover:bg-blue-700 text-white"
                    >
                      {isUpdatingPayment ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {L.saving ?? "Sparar..."}
                        </>
                      ) : (
                        (L.saveChanges ?? "Spara ändringar")
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditingBuyerPayment(false);
                        setBuyerPaymentForm(originalBuyerPaymentForm);
                      }}
                    >
                      {L.cancel ?? btn.cancel}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.paymentStatus ?? "Betalningsstatus"}
                      </p>
                      <Badge
                        variant={
                          buyerPaymentForm.status === "paid"
                            ? "default"
                            : buyerPaymentForm.status === "pending"
                              ? "secondary"
                              : "destructive"
                        }
                      >
                        {buyerPaymentForm.status === "paid"
                          ? (L.paid ?? "Betald")
                          : buyerPaymentForm.status === "pending"
                            ? (L.pendingPayment ?? "Väntar på betalning")
                            : buyerPaymentForm.status === "unpaid"
                              ? (L.unpaid ?? "Obetald")
                              : buyerPaymentForm.status === "failed"
                                ? (L.failed ?? "Misslyckades")
                                : (L.cancelled ?? "Avbruten")}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-muted-foreground">
                          {L.amount ?? "Belopp"}{" "}
                          {buyerSaleBase > 0 && (
                            <span>
                              ({buyerPaymentForm.feePercent.toFixed(1)}%{" "}
                              {L.bidFee ?? "Budavgift"})
                            </span>
                          )}
                        </p>
                        {hasBidFee && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="focus:outline-none"
                              >
                                <Info className="h-4 w-4 text-muted-foreground" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="max-w-xs p-3">
                              <div className="space-y-2.5 text-sm">
                                <div className="font-semibold">
                                  {L.priceBreakdown ?? "Prisuppdelning"}
                                </div>
                                <div className="flex justify-between gap-4">
                                  <span className="text-muted-foreground">
                                    {L.salePrice ?? "Försäljningspris"}:
                                  </span>
                                  <span>
                                    {buyerBaseAmount.toLocaleString("sv-SE")}{" "}
                                    SEK
                                  </span>
                                </div>
                                <div className="flex justify-between gap-4">
                                  <span className="text-muted-foreground">
                                    {L.bidFee ?? "Budavgift"} (
                                    {buyerPaymentForm.feePercent.toFixed(1)}%):
                                  </span>
                                  <span>
                                    {buyerFeeAmount.toLocaleString("sv-SE")} SEK
                                  </span>
                                </div>
                                <div className="flex justify-between gap-4 pt-2 border-t border-border font-semibold">
                                  <span>{L.total ?? "Totalt"}:</span>
                                  <span>
                                    {buyerTotalAmount.toLocaleString("sv-SE")}{" "}
                                    SEK
                                  </span>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                      <p className="text-lg font-semibold">
                        {buyerPaymentForm.amount.toLocaleString()} SEK
                      </p>
                      {suggestedBuyerAmount > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {(invoiceUploadI18n.suggestedAmountLabel ??
                            "Prisförslag") +
                            `: ${Math.round(suggestedBuyerAmount).toLocaleString("sv-SE")} SEK`}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.dueDate ?? "Förfallodatum"}
                      </p>
                      <p className="text-sm">
                        {buyerPayment?.due_date
                          ? new Date(buyerPayment.due_date).toLocaleDateString(
                              "sv-SE",
                            )
                          : (L.notSpecified ?? "Ej angivet")}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.paymentDate ?? "Betalningsdatum"}
                      </p>
                      <p className="text-sm">
                        {buyerPayment?.paid_date
                          ? new Date(buyerPayment.paid_date).toLocaleDateString(
                              "sv-SE",
                            )
                          : (L.notSpecified ?? "Ej angivet")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Kommentar - visa endast när inte i redigeringsläge */}
            {!isEditingBuyerPayment && buyerPaymentForm.comment && (
              <div className="">
                <div className="space-y-2">
                  <p className="font-medium text-blue-500">
                    {L.comment ?? "Kommentar"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {buyerPaymentForm.comment}
                  </p>
                </div>
              </div>
            )}

            {/* Dokument */}
            <div className="border-t pt-4">
              <p className="font-medium text-blue-500 mb-4">
                {L.documents ?? "Dokument"}
              </p>

              {/* Faktura */}
              <Card className="space-y-2 mb-6 p-4 bg-primary/5">
                <div className="flex items-center gap-2 mb-6">
                  <p className="font-medium">{L.invoice ?? "Faktura"}</p>
                  {buyerPayment?.invoice && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleteInvoiceConfirm(true)}
                        className="cursor-pointer text-red-600 hover:text-red-700"
                        disabled={isDeletingInvoice}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {isDeletingInvoice
                          ? (L.deletingInvoice ?? "Raderar...")
                          : (L.deleteInvoice ?? "Radera faktura")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditingInvoice(true)}
                        className="cursor-pointer"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        {L.editInvoice ?? "Redigera faktura"}
                      </Button>
                    </div>
                  )}
                </div>

                {!buyerPayment?.invoice ? (
                  <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                    <FileText className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-3">
                      {L.noInvoiceFound ?? "Ingen faktura hittad"}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowInvoiceUploadDialog(true)}
                      className="cursor-pointer"
                      disabled={isCreatingInvoice}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {isCreatingInvoice
                        ? (L.uploading ?? "Laddar upp...")
                        : (L.uploadNewInvoice ?? "Ladda upp ny faktura")}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.invoiceNumber ?? "Fakturanummer"}
                      </p>
                      <p className="text-sm">
                        {buyerPayment.invoice.invoice_number}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {L.file ?? "Fil"}
                      </p>
                      {buyerPayment.invoice.invoice_file ? (
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-600">
                            {buyerPayment.invoice.invoice_file.name ||
                              "Faktura.pdf"}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleDownloadInvoice}
                            className="cursor-pointer p-1 h-auto"
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleDeleteInvoiceFile}
                            className="cursor-pointer text-red-600 hover:text-red-700 p-1 h-auto"
                            disabled={isDeletingInvoiceFile}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-500">
                            {L.noFile ?? "Ingen fil"}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const input = document.createElement("input");
                              input.type = "file";
                              input.accept = ".pdf,.doc,.docx";
                              input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement)
                                  .files?.[0];
                                if (file) {
                                  handleEditingInvoiceFileUpload(file);
                                }
                              };
                              input.click();
                            }}
                            className="cursor-pointer p-1 h-auto"
                          >
                            <Upload className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>

              {/* Kontrakt */}
              <Card className="space-y-2 p-4 bg-primary/5">
                <div className="flex items-center mb-6 gap-2">
                  <p className="font-medium">{L.contract ?? "Kontrakt"}</p>
                  {buyerPayment?.contract && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleteContractConfirm(true)}
                        className="cursor-pointer text-red-600 hover:text-red-700"
                        disabled={isDeletingContract}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {isDeletingContract
                          ? (L.deletingContract ?? "Raderar...")
                          : (L.deleteContract ?? "Radera kontrakt")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditingContract(true)}
                        className="cursor-pointer"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        {L.editContract ?? "Redigera kontrakt"}
                      </Button>
                    </div>
                  )}
                </div>

                {!buyerPayment?.contract ? (
                  <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
                    <FileText className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-3">
                      {L.noContractFound ?? "Inget kontrakt hittat"}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowContractUploadDialog(true)}
                      className="cursor-pointer"
                      disabled={isCreatingContract}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {isCreatingContract
                        ? (L.uploading ?? "Laddar upp...")
                        : (L.uploadNewContract ?? "Ladda upp ny kontrakt")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">
                          {L.contractNumber ?? "Kontraktsnummer"}
                        </p>
                        <p className="text-sm">
                          {buyerPayment.contract.contract_number}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">
                          {L.file ?? "Fil"}
                        </p>
                        {buyerPayment.contract.contract_file ? (
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-green-600">
                              {buyerPayment.contract.contract_file.name ||
                                "Kontrakt.pdf"}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleDownloadContract}
                              className="cursor-pointer p-1 h-auto"
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleDeleteContractFile}
                              className="cursor-pointer text-red-600 hover:text-red-700 p-1 h-auto"
                              disabled={isDeletingContractFile}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-500">
                              {L.noFile ?? "Ingen fil"}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const input = document.createElement("input");
                                input.type = "file";
                                input.accept = ".pdf,.doc,.docx";
                                input.onchange = (e) => {
                                  const file = (e.target as HTMLInputElement)
                                    .files?.[0];
                                  if (file) {
                                    handleEditingContractFileUpload(file);
                                  }
                                };
                                input.click();
                              }}
                              className="cursor-pointer p-1 h-auto"
                            >
                              <Upload className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                      <div className="mt-4 rounded-lg border bg-background p-4 space-y-4">
                        {contractSignatureStatus === "signed" &&
                          contractSignatureMethod === "bankid_idura" && (
                          <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/50">
                            <div className="flex items-center gap-2 font-medium text-green-900 dark:text-green-100">
                              <img
                                src="/assets/BankID_logo.png"
                                alt={L.contractSignatureMethodBankId ?? "BankID"}
                                className="h-4 w-auto object-contain"
                              />
                              {L.contractSignedBankIdViaIdura ??
                                "Kontraktet är signerat med BankID via Idura"}
                            </div>
                            <p className="mt-1 text-sm text-green-800 dark:text-green-200">
                              {L.contractSignedPdfReplacedUpload ??
                                "Den uppladdade kontraktsfilen har ersatts med den signerade PDF-versionen."}
                            </p>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">
                            {L.contractSignatureStatus ?? "Signeringsstatus"}
                          </p>
                          <Badge
                            className={
                              contractSignatureStatus === "signed"
                                ? "bg-green-100 text-green-800"
                                : contractSignatureStatus === "requested"
                                  ? "bg-amber-100 text-amber-800"
                                  : contractSignatureStatus === "declined"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-gray-100 text-gray-800"
                            }
                          >
                            {getContractSignatureStatusLabel()}
                          </Badge>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">
                            {L.contractSignatureMethod ?? "Metod"}
                          </p>
                          <p className="text-sm">
                            {getContractSignatureMethodLabel(contractSignatureMethod)}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">
                            {contractSignatureStatus === "signed"
                              ? (L.contractSignatureDate ?? "Signerad")
                              : (L.contractSignatureRequestedAt ?? "Begärd")}
                          </p>
                          <p className="text-sm">
                            {contractSignatureStatus === "signed"
                              ? (buyerPayment.contract.buyer_signed_at
                                  ? new Date(buyerPayment.contract.buyer_signed_at).toLocaleString("sv-SE")
                                  : (L.notSpecified ?? "Ej angivet"))
                              : (buyerPayment.contract.buyer_signature_requested_at
                                  ? new Date(
                                      buyerPayment.contract.buyer_signature_requested_at,
                                    ).toLocaleString("sv-SE")
                                  : (L.notSpecified ?? "Ej angivet"))}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">
                            {L.contractSignatureSignedBy ?? "Signerad av"}
                          </p>
                          <p className="text-sm">
                            {contractSignedByName || (L.notSpecified ?? "Ej angivet")}
                          </p>
                        </div>
                      </div>

                      {buyerPayment.contract.buyer_signature_note && (
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">
                            {L.contractSignatureNote ?? "Notering"}
                          </p>
                          <p className="text-sm">
                            {buyerPayment.contract.buyer_signature_note}
                          </p>
                        </div>
                      )}

                      {contractSignatureStatus === "signed" ? (
                        <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/50">
                          <p className="font-medium text-green-900 dark:text-green-100">
                            {L.contractSignatureSigned ?? "Signerat"}
                          </p>
                          <p className="mt-1 text-sm text-green-800 dark:text-green-200">
                            {L.buyerSignatureCompletedInfo ??
                              "Kontraktet är färdigt signerat. Inga ytterligare åtgärder krävs från admin."}
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="rounded-lg border p-3 space-y-3">
                            <div>
                              <p className="font-medium">
                                {L.requestContractSignature ?? "Begär BankID-signering"}
                              </p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {L.requestContractSignatureDesc ??
                                  "Skickar mejl och notis till köparen. Knappen för BankID-signering visas sedan i orders."}
                              </p>
                            </div>
                            <div className={`rounded-md border p-3 ${buyerVerificationHighlightClass}`}>
                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                {buyerIdentityVerificationMethod === "bankid" ? (
                                  <Badge className="gap-1.5 bg-green-600 text-white hover:bg-green-600">
                                    <img
                                      src="/assets/BankID_logo.png"
                                      alt="BankID"
                                      className="h-3.5 w-auto object-contain"
                                    />
                                    {L.contractSignatureMethodBankId ?? "BankID"}
                                  </Badge>
                                ) : buyerIdentityVerificationMethod === "didit" ? (
                                  <Badge variant="outline" className="gap-1.5 border-blue-500 text-blue-700">
                                    <ScanFace className="h-3.5 w-3.5" />
                                    {L.buyerIdentityDiditMethod ?? "ID-handling + livefoto"}
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="border-amber-500 text-amber-700">
                                    {L.notSpecified ?? "Ej verifierad"}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm font-medium">
                                {L.buyerIdentityVerificationLabel ?? "Köparens identitetsverifiering"}
                              </p>
                              <p className="mt-1 text-sm text-muted-foreground">
                                {buyerIdentityVerificationMethod === "bankid"
                                  ? (L.buyerIdentityVerifiedWithBankId ??
                                      "Köparen är ID-verifierad med BankID och kan signera kontraktet med BankID.")
                                  : buyerIdentityVerificationMethod === "didit"
                                    ? (L.buyerIdentityVerifiedWithDidit ??
                                        "Köparen är ID-verifierad via Didit men inte verifierad med BankID ännu. Du kan ändå begära BankID-signering här.")
                                    : (L.buyerIdentityNotVerified ??
                                        "Köparen är inte ID-verifierad med BankID eller Didit ännu. Du kan ändå begära BankID-signering här.")}
                              </p>
                            </div>
                            <Button
                              onClick={handleRequestContractSignature}
                              disabled={
                                !buyerPayment.contract.contract_file ||
                                isRequestingSignature
                              }
                              className="gap-2"
                            >
                              <img
                                src="/assets/BankID_logo.png"
                                alt="BankID"
                                className="h-4 w-auto object-contain"
                              />
                              {isRequestingSignature
                                ? (L.requestingContractSignature ?? "Begär...")
                                : (L.requestContractSignature ?? "Begär BankID-signering")}
                            </Button>
                          </div>

                          <div className="rounded-lg border p-3 space-y-3">
                            <div>
                              <p className="font-medium">
                                {L.markContractSignature ?? "Markera signerat manuellt"}
                              </p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {L.markContractSignatureDesc ??
                                  "Använd detta om kontraktet signerats eller hanterats utanför BankID-flödet."}
                              </p>
                            </div>
                            <Select
                              value={manualSignatureMethod}
                              onValueChange={(value) =>
                                setManualSignatureMethod(
                                  value as
                                    | "physical_id_check"
                                    | "signed_externally"
                                    | "no_signature_required"
                                    | "other",
                                )
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="physical_id_check">
                                  {L.contractSignatureMethodPhysical ??
                                    "Fysiskt signerat med ID-kontroll"}
                                </SelectItem>
                                <SelectItem value="signed_externally">
                                  {L.contractSignatureMethodExternal ??
                                    "Signerat utanför systemet"}
                                </SelectItem>
                                <SelectItem value="no_signature_required">
                                  {L.contractSignatureMethodNotRequired ??
                                    "Signering behövs ej"}
                                </SelectItem>
                                <SelectItem value="other">
                                  {L.contractSignatureMethodOther ??
                                    "Annan bekräftad metod"}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <Textarea
                              value={manualSignatureNote}
                              onChange={(e) => setManualSignatureNote(e.target.value)}
                              placeholder={
                                L.contractSignatureNotePlaceholder ??
                                "Valfri intern notering om hur kontraktet signerades."
                              }
                            />
                            <Button
                              variant="outline"
                              onClick={handleMarkManualContractSignature}
                              disabled={isMarkingSignature}
                            >
                              {isMarkingSignature
                                ? (L.markingContractSignature ?? "Uppdaterar...")
                                : (L.markContractSignature ?? "Markera signerat manuellt")}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contract Edit Dialog */}
      {isEditingContract && buyerPayment?.contract && (
        <Dialog open={isEditingContract} onOpenChange={setIsEditingContract}>
          <DialogContent nested className="max-w-md">
            <DialogHeader>
              <DialogTitle>{L.editContract ?? "Redigera kontrakt"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="contract-number">
                  {L.contractNumber ?? "Kontraktsnummer"}
                </Label>
                <Input
                  id="contract-number"
                  value={contractEditForm.contract_number}
                  onChange={(e) => {
                    setContractEditForm((prev) => ({
                      ...prev,
                      contract_number: e.target.value,
                    }));
                    setHasContractChanges(true);
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsEditingContract(false)}
              >
                {L.cancel ?? btn.cancel}
              </Button>
              <Button
                onClick={handleSaveContractEdit}
                disabled={!hasContractChanges || !isContractFormValid()}
              >
                Spara ändringar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Invoice Edit Dialog */}
      {isEditingInvoice && buyerPayment?.invoice && (
        <Dialog open={isEditingInvoice} onOpenChange={setIsEditingInvoice}>
          <DialogContent nested className="max-w-md">
            <DialogHeader>
              <DialogTitle>Redigera faktura</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="invoice-number">Fakturanummer</Label>
                <Input
                  id="invoice-number"
                  value={invoiceEditForm.invoice_number}
                  onChange={(e) => {
                    setInvoiceEditForm((prev) => ({
                      ...prev,
                      invoice_number: e.target.value,
                    }));
                    setHasInvoiceChanges(true);
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsEditingInvoice(false)}
              >
                {L.cancel ?? btn.cancel}
              </Button>
              <Button
                onClick={handleSaveInvoiceEdit}
                disabled={!hasInvoiceChanges || !isInvoiceFormValid()}
              >
                Spara ändringar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Invoice Upload Dialog */}
      <InvoiceUploadDialog
        open={showInvoiceUploadDialog}
        onOpenChange={setShowInvoiceUploadDialog}
        listingId={buyerPayment?.listingId}
        suggestedAmount={suggestedBuyerAmount}
        onSuccessWithMeta={async (invoice, meta) => {
          setShowInvoiceUploadDialog(false);
          // Update UI with the created invoice
          if (buyerPayment) {
            buyerPayment.invoice = invoice;
          }

          // Save entered amount as the final buyer payment amount (shown in /orders)
          try {
            if (buyerPayment) {
              await updatePaymentAsync({
                id: buyerPayment.id,
                listingId: buyerPayment.listingId,
                type: buyerPayment.type,
                status: buyerPayment.status,
                amount: meta.amount,
                comment: buyerPayment.comment || "",
                due_date: meta.dueDate || null,
                paid_date: buyerPayment.paid_date || null,
                payout_bankDetails: buyerPayment.payout_bankDetails,
                created_at: buyerPayment.created_at,
              });
            }
          } catch (e) {
            const err = e as Partial<ApiError> | undefined;
            toast.error(
              (L.errorSaving ?? "Kunde inte spara") +
                `: ${err?.message || "Okänt fel"}`,
            );
          }

          toast.success(L.invoiceUploaded ?? "Faktura uppladdad!");
        }}
        onSuccess={(invoice) => {
          // keep legacy handler to ensure no callers break
          if (buyerPayment) buyerPayment.invoice = invoice;
        }}
      />

      {/* Contract Upload Dialog */}
      <ContractUploadDialog
        open={showContractUploadDialog}
        onOpenChange={setShowContractUploadDialog}
        listingId={buyerPayment?.listingId}
        onSuccess={(contract) => {
          setShowContractUploadDialog(false);
          // Update UI with the created contract
          if (buyerPayment) {
            buyerPayment.contract = contract;
          }
          toast.success(L.contractUploaded ?? "Kontrakt uppladdat!");
        }}
      />

      {/* Seller Contract Upload Dialog */}
      <ContractUploadDialog
        open={showSellerContractUploadDialog}
        onOpenChange={setShowSellerContractUploadDialog}
        listingId={contractListingId ?? undefined}
        party="seller"
        onSellerContractCreated={() => {
          setShowSellerContractUploadDialog(false);
          void refetchSellerContract();
        }}
      />

      {/* Delete Invoice Confirmation Dialog */}
      <Dialog
        open={showDeleteInvoiceConfirm}
        onOpenChange={setShowDeleteInvoiceConfirm}
      >
        <DialogContent nested className="max-w-md">
          <DialogHeader>
            <DialogTitle>Radera faktura</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              {L.areYouSure ?? "Är du säker?"}{" "}
              {L.thisWillDeletePermanently ??
                "Detta kommer ta bort objektet direkt och går inte att ändra."}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteInvoiceConfirm(false)}
            >
              {L.cancel ?? btn.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteInvoice}
              disabled={isDeletingInvoiceFile}
            >
              {isDeletingInvoiceFile ? "Raderar..." : "Radera faktura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Contract Confirmation Dialog */}
      <Dialog
        open={showDeleteContractConfirm}
        onOpenChange={setShowDeleteContractConfirm}
      >
        <DialogContent nested className="max-w-md">
          <DialogHeader>
            <DialogTitle>{L.deleteContract ?? "Radera kontrakt"}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              {L.areYouSure ?? "Är du säker?"}{" "}
              {L.thisWillDeletePermanently ??
                "Detta kommer ta bort objektet direkt och går inte att ändra."}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteContractConfirm(false)}
            >
              {L.cancel ?? btn.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteContract}
              disabled={isDeletingContractFile}
            >
              {isDeletingContractFile
                ? (L.deletingContract ?? "Raderar...")
                : (L.deleteContract ?? "Radera kontrakt")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

